/**
 * @name ProtectedRoute.js
 * @author 김권영
 * @since 2025. 01. 06.
 * @description
 * AppRoutes.js 의 URL 접근 권한 Procted Route
 * 
 * @param {string} roles - 권한
 */
import React, { useContext, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthContext } from '@context';
import useMyMenus from '@hooks/comm/useMyMenus';

const AlertAndRedirect = ({ message, to }) => {
    useEffect(() => {
        toast.error(message);
    }, [message]);

    return <Navigate to={to} replace />;
};

const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-screen text-gray-400 text-sm">
        <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mr-2" />
        로딩 중...
    </div>
);

// 메뉴 트리에서 모든 path 추출 (재귀)
const flattenMenuPaths = (menus) => {
    const paths = [];
    const traverse = (nodes) => {
        nodes.forEach(menu => {
            if (menu.path) paths.push(menu.path);
            if (menu.children?.length) traverse(menu.children);
        });
    };
    traverse(menus);
    return paths;
};

/**
 * @param {string[]} roles              - 허용 역할 목록 (ROLES 상수 사용)
 * @param {string}   requiredPermission - 필요한 세분화 권한 코드 (선택)
 * @param {string}   menuPath           - DB ATOS_MENU.PATH 값 (선택, 메뉴 할당 여부 검사)
 */
const ProtectedRoute = ({ children, roles, requiredPermission, menuPath }) => {
    const { auth, isLoading, hasPermission } = useContext(AuthContext);
    const { data: myMenus = [], isLoading: menuLoading } = useMyMenus(auth.isAuthenticated);

    if (isLoading || (menuPath && menuLoading)) {
        return <LoadingSpinner />;
    }

    if (!auth.isAuthenticated) {
        return <AlertAndRedirect message="로그인이 필요합니다." to="/" />;
    }

    // 임시 비밀번호 로그인 시 홈으로 리다이렉트 (모달에서 비밀번호 변경 처리)
    if (auth.forceChangePw === 'Y') {
        return <Navigate to="/" replace />;
    }

    if (roles && !roles.includes(auth.role)) {
        return <AlertAndRedirect message="권한이 없습니다" to="/main" />;
    }

    if (requiredPermission && !hasPermission(requiredPermission)) {
        return <AlertAndRedirect message="권한이 없습니다" to="/main" />;
    }

    // 메뉴 할당 여부 체크: menuPath prop이 있을 때만 검사
    if (menuPath) {
        const allowedPaths = flattenMenuPaths(myMenus);
        if (!allowedPaths.includes(menuPath)) {
            return <AlertAndRedirect message="접근 권한이 없습니다." to="/main" />;
        }
    }

    return children;
};

export default ProtectedRoute;
